import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useAccountData, useStorefrontData } from '../StorefrontThemeWrapper';
import { StorefrontHeader } from '../common/StorefrontHeader';
import { StorefrontFooter } from '../common/StorefrontFooter';
import { AboutSection } from '../storefrontPage/aboutSection/AboutSection';
import { HeroGallery } from './components/HeroGallery';
import { DescriptionSection } from './components/DescriptionSection';
import { WhatsIncludedSection } from './components/WhatsIncludedSection';
import { PracticalInfoSection } from './components/PracticalInfoSection';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageImageData, PartialLabelData } from '../../api';
import { getPrimaryPhoto } from '../../utils/orderPageImageUtils';

type ExperiencePageLayoutProps = {
  passOrOrderPage: {
    label?: PartialLabelData;
    description?: PartialLabelData;
    order_page_images?: Array<OrderPageImageData>;
    order_page_items_descriptions?: Array<PartialLabelData>;
    practical_info_descriptions?: Array<PartialLabelData>;
  };
  header: ReactNode;
  renderBookingWidget: (storefrontStyling: any) => ReactNode;
  renderMobileBar: (scrollToWidget: () => void) => ReactNode;
  renderSchedule?: () => ReactNode;
  renderRelated?: () => ReactNode;
  hideImages?: boolean;
};

export const ExperiencePageLayout = ({
  passOrOrderPage,
  header,
  renderBookingWidget,
  renderMobileBar,
  renderSchedule,
  renderRelated,
  hideImages,
}: ExperiencePageLayoutProps) => {
  const account = useAccountData();
  const storefront = useStorefrontData();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const bookingWidgetRef = useRef<HTMLDivElement>(null);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);

  useEffect(() => {
    const el = bookingWidgetRef.current;
    // eslint-disable-next-line consistent-return
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => setIsWidgetVisible(entry.isIntersecting), {
      threshold: 0.1,
    });
    observer.observe(el);
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, []);

  const businessDetails = account?.business_details;
  const experienceName = passOrOrderPage.label?.[language] || '';
  const { description, order_page_images: images = [] } = passOrOrderPage;
  const whatsIncludedItems = passOrOrderPage.order_page_items_descriptions || [];
  const practicalInfoItems = passOrOrderPage.practical_info_descriptions || [];

  const scrollToBookingWidget = () => {
    bookingWidgetRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Grid
      container
      flexDirection="column"
      sx={{
        maxWidth: 1300,
        margin: '0 auto',
        padding: isSmallScreen ? '0 16px' : '0 80px',
        paddingBottom: isSmallScreen ? '90px' : 0,
      }}>
      <StorefrontHeader />

      {!hideImages && (
        <HeroGallery
          images={images}
          experienceName={experienceName}
          fallbackImage={getPrimaryPhoto(passOrOrderPage as any, isSmallScreen ? 600 : 1300)}
        />
      )}

      <Grid
        container
        sx={{
          flexDirection: isSmallScreen ? 'column' : 'row',
          gap: isSmallScreen ? 0 : 6,
          marginTop: isSmallScreen ? 3 : 5,
        }}>
        {/* Content Column */}
        <Box
          sx={{
            flex: '1 1 60%',
            minWidth: 0,
          }}>
          {header}

          {description && <DescriptionSection description={description} />}

          {whatsIncludedItems.length > 0 && <WhatsIncludedSection items={whatsIncludedItems} />}

          {practicalInfoItems.length > 0 && <PracticalInfoSection items={practicalInfoItems} />}

          {renderSchedule && renderSchedule()}

          {renderRelated && renderRelated()}

          {isSmallScreen && (
            <Box ref={bookingWidgetRef} sx={{ marginTop: 4 }}>
              {renderBookingWidget(storefront?.styling)}
            </Box>
          )}
        </Box>

        {/* Widget Column - Desktop Only */}
        {!isSmallScreen && (
          <Box
            sx={{
              width: 390,
              flexShrink: 0,
            }}>
            <Box
              ref={bookingWidgetRef}
              sx={{
                position: 'sticky',
                top: 40,
              }}>
              {renderBookingWidget(storefront?.styling)}
            </Box>
          </Box>
        )}
      </Grid>

      <Grid container sx={{ marginTop: isSmallScreen ? 6 : 8 }}>
        <AboutSection businessDetails={businessDetails} accountName={account?.label?.[language] || ''} />
      </Grid>

      <StorefrontFooter businessDetails={businessDetails} />

      {isSmallScreen && !isWidgetVisible && renderMobileBar(scrollToBookingWidget)}
    </Grid>
  );
};
